import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  pipe,
  tap,
  switchMap,
  catchError,
  forkJoin,
  debounceTime,
  of,
  EMPTY,
} from 'rxjs';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EquipmentAsyncService } from 'src/app/app-services-async/equipment-async.service';
import {
  FLOW_GATEWAY,
  IWHBomNodeDTO,
  IWHBomNodeSkillDTO,
  IWHEquipmentDTO,
  IWHEquipmentEndpointConfigModel,
  WHBomNodeDataService,
  WHEquipmentDataService,
  WHFeatureKeyENUM,
  WHLoginDataService,
  WHFileUploadDataService,
  IWHBomNodeEndpointConfigModel,
  WHRouteZeroENUM,
  WHToastService,
} from '@workheld/workheld-shared-lib';
import { environment } from 'src/environments/environment';
import { WHNestedBomNodeDOM } from 'src/app/w-h-module/w-h-bom-node-crud-form/w-h-nested-node.model';
import { sortFlexProperties } from 'src/app/app-services/form.service';
import { Router } from '@angular/router';
import { FormReferenceService } from 'src/app/app-services-helper/form-reference.service';
import { handleGlobalError } from 'src/app/app-shared/operators/global-error.operator';

export enum DOCUMENTATION_ITEM_TYPE {
  ENTRIES = '0',
  ENTRY_FILES = '1',
  REPORT_FILES = '2',
  CHECKLIST_INSTANCES = '3',
  EVENT_LOGS = '4',
  TIME_ENTRIES = '5',
}

export type EquipmentViewMode = 'detail' | 'edit' | 'create';

type EquipmentState = {
  equipment: IWHEquipmentDTO;
  currentNode: IWHBomNodeDTO & { rootNodeId?: string };
  isLoading: boolean;
  isTreeLoading: boolean;
  viewMode: EquipmentViewMode;
  createConfig: {
    sortPosition?: number;
    parentNodeId?: string;
    method: 'CREATE' | 'UPDATE';
  };
  shouldRefreshSkillCount: IWHBomNodeDTO & { isUnassign?: boolean };
  activeWorkTabIndex: number;
  activeDetailTabIndex: number;
  assignmentInProcess: boolean;
  navHistory: string[];
  documentCounts: Map<number, number>;
  activeAssignmentTabIndex: number;
  shouldRefetch: boolean;
  nodeTree: WHNestedBomNodeDOM | null;
  nodeTrees: WHNestedBomNodeDOM[];
  nodesMap: Map<string, WHNestedBomNodeDOM>;
  /**
   * `true` when the last structure request failed (WHP-12588), so the tree can
   * show an error state instead of looking like an equipment without children.
   */
  treeLoadError: boolean;

  showNodeIdEnabled: boolean;
  showDecommissionedNodesEnabled: boolean;
};

const initialState: EquipmentState = {
  equipment: null,
  currentNode: null,
  isLoading: false,
  isTreeLoading: false,
  viewMode: 'detail',
  createConfig: null,
  activeWorkTabIndex: 0,
  shouldRefreshSkillCount: null,
  assignmentInProcess: false,
  activeDetailTabIndex: 0,
  activeAssignmentTabIndex: 0,
  navHistory: [],
  documentCounts: new Map<number, number>(),
  shouldRefetch: false,
  nodeTree: null,
  nodeTrees: [],
  nodesMap: new Map(),
  treeLoadError: false,

  showNodeIdEnabled: false,
  showDecommissionedNodesEnabled: false,
};

export function qrCodeFileName(name, id) {
  return 'qr-' + name + '_' + id;
}

export const EquipmentStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withComputed((store) => ({
    isEdit: computed(() => store.viewMode() === 'edit'),
    isRootNode: computed(() => {
      return store.equipment()?.rootBomNodeId === store.currentNode()?.id;
    }),
    targetParent: computed(() => {
      const isRootNode =
        store.equipment()?.rootBomNodeId === store.currentNode()?.id;
      return isRootNode ? 'EQUIPMENT' : 'BOMNODE';
    }),
    targetId: computed(() => {
      const isRootNode =
        store.equipment()?.rootBomNodeId === store.currentNode()?.id;
      return isRootNode ? store.equipment()?.id : store.currentNode()?.id;
    }),
    isDecommissionedNode: computed(() => {
      return store.currentNode()?.entityStatus === 'INACTIVE';
    }),
    /**
     * `includeDecommissioned` to send with every structure request
     * (WHP-12588).
     *
     * Decommissioning turns the whole subtree INACTIVE, so honouring the
     * "include decommissioned" toggle would answer with no children at all and
     * leave the Structure tab empty and unnavigable. Decommissioning marks an
     * equipment read-only, it does not hide its structure — so the flag is
     * forced on for a decommissioned node, whatever the toggle says.
     */
    includeDecommissionedNodes: computed(() => {
      return (
        store.showDecommissionedNodesEnabled() ||
        store.currentNode()?.entityStatus === 'INACTIVE'
      );
    }),
    isUsedNode: computed(() => {
      return store.currentNode()?.used;
    }),
    parentNodeId: computed(() => {
      return (
        store.currentNode()?.parentNodeId ?? store.currentNode()?.rootNodeId
      );
    }),
    nodeLevel: computed(() => {
      return (store.nodesMap().get(store.currentNode()?.id) as any)?.level || 0;
    }),
    qrCodeName: computed(() => {
      return store.equipment()?.rootBomNodeId !== store.currentNode()?.id
        ? qrCodeFileName(store.currentNode()?.name, store.currentNode()?.extId)
        : qrCodeFileName(
            store.equipment()?.name,
            store.equipment()?.equipmentNumber,
          );
    }),
    navigationNodeId: computed(() => {
      const history = store.navHistory();
      return history.length ? history[history.length - 1] : null;
    }),
  })),
  withMethods(
    (
      store,
      equipmentService = inject(EquipmentAsyncService),
      bomNodeDataService = inject(WHBomNodeDataService),
      equipmentDataService = inject(WHEquipmentDataService),
      fileUploadDataService = inject(WHFileUploadDataService),
      skillsService = inject(WHLoginDataService),
      toastrService = inject(WHToastService),
      router = inject(Router),
      formReferenceService = inject(FormReferenceService),
    ) => {
      const buildPictureUploadMetadata = (imageFile: {
        blob: Blob;
        file: File;
      }) => ({
        fileName: imageFile.file?.name,
        mimeType: imageFile.file?.type || imageFile.blob.type,
      });

      const applyTreeSkills = (
        nodesMap: Map<string, WHNestedBomNodeDOM>,
        skillsByNodeId: Record<string, IWHBomNodeSkillDTO[]>,
      ) => {
        nodesMap.forEach((node, nodeId) => {
          node.skills = skillsByNodeId[nodeId] ?? [];
        });
      };

      const uploadPictureToTargets = (
        targetIds: {
          bomNodeId?: string;
          equipmentId?: string;
        },
        imageFile: { blob: Blob; file: File },
      ) => {
        const uploadTargets = [];
        if (targetIds.bomNodeId) {
          uploadTargets.push({
            targetType: 'BOM_NODE_PICTURE',
            targetId: targetIds.bomNodeId,
          });
        }
        if (targetIds.equipmentId) {
          uploadTargets.push({
            targetType: 'EQUIPMENT',
            targetId: targetIds.equipmentId,
          });
        }
        return fileUploadDataService.uploadFileToTargets(
          {
            apiUrl: environment.apiUrl + FLOW_GATEWAY,
          },
          uploadTargets,
          imageFile.blob,
          buildPictureUploadMetadata(imageFile),
        );
      };

      return {
        resetStore(): void {
          patchState(store, {
            ...initialState,
            showNodeIdEnabled: store.showNodeIdEnabled(),
            showDecommissionedNodesEnabled:
              store.showDecommissionedNodesEnabled(),
          });
        },
        setViewMode(viewMode: EquipmentViewMode): void {
          patchState(store, { viewMode });
        },
        setIsEdit(isEdit: boolean): void {
          patchState(store, { viewMode: isEdit ? 'edit' : 'detail' });
        },
        setShowNodeIdEnabled(showNodeIdEnabled: boolean): void {
          patchState(store, { showNodeIdEnabled });
        },
        setShowDecommissionedNodesEnabled(
          showDecommissionedNodesEnabled: boolean,
        ): void {
          patchState(store, { showDecommissionedNodesEnabled });
        },
        clearNodeTree(): void {
          patchState(store, {
            nodeTree: null,
            nodeTrees: [],
            nodesMap: new Map(),
            isTreeLoading: false,
            treeLoadError: false,
          });
        },
        setActiveDetailTabIndex(activeDetailTabIndex: number): void {
          patchState(store, { activeDetailTabIndex });
        },
        setActiveWorkTabIndex(activeWorkTabIndex: number): void {
          patchState(store, { activeWorkTabIndex });
        },
        setActiveAssignmentTabIndex(activeAssignmentTabIndex: number): void {
          patchState(store, { activeAssignmentTabIndex });
        },
        setShouldRefreshSkillCount(
          shouldRefresh: IWHBomNodeDTO & { isUnassign?: boolean },
        ): void {
          patchState(store, { shouldRefreshSkillCount: shouldRefresh });
        },
        setAssignmentInProcess(assignmentInProcess: boolean): void {
          patchState(store, { assignmentInProcess });
        },
        appendNavHistory(bomNodeId: string) {
          const newHistory = [...store.navHistory(), bomNodeId];

          patchState(store, { navHistory: newHistory });
        },
        removeLastNodeFromNavHistory() {
          const newHistory = store.navHistory().slice(0, -1);
          patchState(store, { navHistory: newHistory });
        },
        setCreateConfig(
          createConfig: {
            sortPosition?: number;
            parentNodeId?: string;
            method: 'CREATE' | 'UPDATE';
          } | null,
        ): void {
          patchState(store, { createConfig });
        },
        loadEquipmentNode: rxMethod<{
          equipment: IWHEquipmentDTO;
          nodeId: string;
        }>(
          pipe(
            debounceTime(200),
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ equipment, nodeId }) => {
              const hasSkillManagementEnabled =
                skillsService.featureConfigMap$.value.get(
                  WHFeatureKeyENUM.SKILL_MANAGEMENT,
                );
              const endpointConfig = {
                apiUrl: environment.apiUrl + FLOW_GATEWAY,
                bomNodeId: nodeId,
              };
              return bomNodeDataService
                .getBomNodeDTO(endpointConfig, hasSkillManagementEnabled)
                .pipe(
                  tap((currentNode) => {
                    patchState(store, {
                      equipment,
                      currentNode: {
                        ...currentNode,
                        flexProperties: sortFlexProperties(
                          currentNode?.flexProperties,
                        ),
                      },
                      isLoading: false,
                      shouldRefetch: false,
                    });
                  }),
                  handleGlobalError({
                    onError: () =>
                      patchState(store, {
                        isLoading: false,
                        shouldRefetch: false,
                      }),
                  }),
                );
            }),
          ),
        ),
        loadEquipment: rxMethod<{ bomNodeId?: string }>(
          pipe(
            debounceTime(200),
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ bomNodeId = store.currentNode()?.id }) => {
              return forkJoin({
                equipment:
                  equipmentService.getEquipmentDtoByBomNodeId(bomNodeId),
                currentNode: bomNodeDataService.getBomNodeDTO(
                  {
                    apiUrl: environment.apiUrl + FLOW_GATEWAY,
                    bomNodeId,
                  },
                  skillsService.featureConfigMap$.value.get(
                    WHFeatureKeyENUM.SKILL_MANAGEMENT,
                  ),
                ),
              }).pipe(
                tap(({ equipment, currentNode }) => {
                  patchState(store, {
                    equipment,
                    currentNode: {
                      ...currentNode,
                      flexProperties: sortFlexProperties(
                        currentNode?.flexProperties,
                      ),
                    },
                    isLoading: false,
                    shouldRefetch: false,
                  });
                }),
                handleGlobalError({
                  onError: () =>
                    patchState(store, {
                      isLoading: false,
                      shouldRefetch: false,
                    }),
                }),
              );
            }),
          ),
        ),
        loadNodeDocumentCounts: rxMethod<void>(
          pipe(
            switchMap(() => {
              const currentNode = store.currentNode();
              if (!currentNode) return EMPTY;
              return bomNodeDataService
                .getDocumentationCount({
                  apiUrl: environment.apiUrl + FLOW_GATEWAY,
                  bomNodeId: currentNode.id,
                })
                .pipe(
                  tap((counts) => {
                    const documentationCount = [
                      {
                        type: Number(DOCUMENTATION_ITEM_TYPE.ENTRIES),
                        count: counts.entries,
                      },
                      {
                        type: Number(DOCUMENTATION_ITEM_TYPE.ENTRY_FILES),
                        count: counts.entryFiles,
                      },
                      {
                        type: Number(DOCUMENTATION_ITEM_TYPE.REPORT_FILES),
                        count: counts.reports,
                      },
                      {
                        type: Number(
                          DOCUMENTATION_ITEM_TYPE.CHECKLIST_INSTANCES,
                        ),
                        count: counts.checklists,
                      },
                      {
                        type: Number(DOCUMENTATION_ITEM_TYPE.EVENT_LOGS),
                        count: counts.eventLogs,
                      },
                      {
                        type: Number(DOCUMENTATION_ITEM_TYPE.TIME_ENTRIES),
                        count: (counts as any).timeEntries,
                      },
                    ];

                    patchState(store, {
                      documentCounts: new Map(
                        documentationCount.map((item) => [
                          item.type,
                          item.count,
                        ]),
                      ),
                    });
                  }),
                  handleGlobalError(),
                );
            }),
          ),
        ),
        decomissionEquipment: rxMethod<void>(
          pipe(
            switchMap(() => {
              const currentNode = store.currentNode();
              return bomNodeDataService
                .deleteDecommissionBomNode(
                  {
                    apiUrl: environment.apiUrl + FLOW_GATEWAY,
                    bomNodeId: currentNode.id,
                  },
                  true,
                )
                .pipe(
                  tap(() => {
                    patchState(store, {
                      currentNode: {
                        ...currentNode,
                        entityStatus: 'INACTIVE',
                      },
                      shouldRefetch: true,
                      nodeTree: null,
                      nodeTrees: [],
                    });
                  }),
                  handleGlobalError(),
                );
            }),
          ),
        ),
        loadFullBomNodeTree: rxMethod<{
          bomNodeId: string;
          includeDecommissioned?: boolean;
        }>(
          pipe(
            tap(({ bomNodeId }) => {
              patchState(store, {
                isTreeLoading: true,
                treeLoadError: false,
                // Drop another root's tree so a failed load shows the error
                // state; a same-root refresh keeps the tree on failure.
                ...(store.nodeTree()?.id !== bomNodeId && {
                  nodeTree: null,
                  nodeTrees: [],
                  nodesMap: new Map(),
                }),
              });
            }),
            switchMap(({ bomNodeId, includeDecommissioned = false }) => {
              const endpointConfig = {
                apiUrl: environment.apiUrl + FLOW_GATEWAY,
                bomNodeId,
              };
              const params = { includeDecommissioned };
              const currentNode = store.currentNode();
              const rootNode$ =
                currentNode?.id === bomNodeId
                  ? of(currentNode)
                  : bomNodeDataService.getBomNodeDTO(endpointConfig, false);

              // Tree and skills (WHP-11105) are loaded together so the tree is
              // only built once, with its skills already in place.
              return forkJoin({
                rootNode: rootNode$,
                treeResponse: bomNodeDataService.getWholeBomNodeTree(
                  endpointConfig,
                  params,
                ),
                skillsByNodeId: bomNodeDataService
                  .getBomNodeTreeSkills(endpointConfig, includeDecommissioned)
                  .pipe(
                    // Skills are supplementary — the tree stays usable without them.
                    catchError(() => of(null)),
                  ),
              }).pipe(
                tap(({ rootNode, treeResponse, skillsByNodeId }) => {
                  const nodesMap = new Map<string, WHNestedBomNodeDOM>();

                  const normalizeTree = (raw: any): WHNestedBomNodeDOM => {
                    // Strip the recursive children before construction —
                    // WHNestedBomNodeDOM copies every property onto itself,
                    // so leaving them in would duplicate the whole subtree.
                    const { childNodes, children, ...flat } = raw ?? {};
                    const rawChildren = Array.isArray(childNodes)
                      ? childNodes
                      : Array.isArray(children)
                        ? children
                        : [];

                    const domNode = new WHNestedBomNodeDOM(
                      flat as IWHBomNodeDTO,
                    );

                    const childDomNodes = rawChildren
                      .map((child) => normalizeTree(child))
                      .sort(
                        (a, b) => (a.sortPosition ?? 0) - (b.sortPosition ?? 0),
                      );

                    domNode.children = childDomNodes;
                    nodesMap.set(domNode.id, domNode);
                    return domNode;
                  };

                  const returnedTrees = (treeResponse?.trees ?? [])
                    .map((rawRoot) => normalizeTree(rawRoot))
                    .sort(
                      (a, b) => (a.sortPosition ?? 0) - (b.sortPosition ?? 0),
                    );

                  // The tree endpoint returns descendants only, so wrap them
                  // under the requested root node.
                  const rootDom = new WHNestedBomNodeDOM(rootNode);
                  const rootChildren = returnedTrees.filter(
                    (node) => node.id !== rootDom.id,
                  );
                  rootDom.children = rootChildren;
                  nodesMap.set(rootDom.id, rootDom);

                  if (skillsByNodeId) {
                    applyTreeSkills(nodesMap, skillsByNodeId);
                  }

                  patchState(store, {
                    nodeTree: rootDom,
                    nodeTrees: [rootDom],
                    nodesMap,
                    isTreeLoading: false,
                  });
                }),
                handleGlobalError({
                  onError: () =>
                    patchState(store, {
                      isTreeLoading: false,
                      treeLoadError: true,
                    }),
                }),
              );
            }),
          ),
        ),

        updateEquipment: rxMethod<{
          endpointConfig: IWHEquipmentEndpointConfigModel;
          payload: Partial<IWHEquipmentDTO>;
          imageFile?: { blob: Blob; file: File };
          removeImage?: boolean;
        }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ endpointConfig, payload, imageFile, removeImage }) => {
              return equipmentDataService
                .updateEquipment(endpointConfig, payload)
                .pipe(
                  switchMap((updatedEquipment) => {
                    // Handle image removal
                    if (removeImage && updatedEquipment.rootBomNodeId) {
                      const bomNodeEndpointConfig: IWHBomNodeEndpointConfigModel =
                        {
                          apiUrl: environment.apiUrl + FLOW_GATEWAY,
                          bomNodeId: updatedEquipment.rootBomNodeId,
                        };
                      return bomNodeDataService
                        .removeImageFile(bomNodeEndpointConfig)
                        .pipe(
                          switchMap(() =>
                            equipmentService.removeImageFile(
                              updatedEquipment.id,
                            ),
                          ),
                          tap(() => {
                            updatedEquipment.pictureURL = undefined;
                            updatedEquipment.thumbnailURL = undefined;
                            updatedEquipment.equipmentPictureFile = null;
                          }),
                          switchMap(() => of(updatedEquipment)),
                          catchError((err) => {
                            // Still return the updated equipment if image removal fails
                            return of(updatedEquipment);
                          }),
                        );
                    }

                    // Handle image upload
                    if (imageFile && updatedEquipment.rootBomNodeId) {
                      return uploadPictureToTargets(
                        {
                          bomNodeId: updatedEquipment.rootBomNodeId,
                          equipmentId: updatedEquipment.id,
                        },
                        imageFile,
                      ).pipe(
                        switchMap(() =>
                          equipmentService.getEquipmentDtoByEquimentId(
                            updatedEquipment.id,
                          ),
                        ),
                        catchError((err) => {
                          // Still return the updated equipment if image upload fails
                          return of(updatedEquipment);
                        }),
                      );
                    }

                    return of(updatedEquipment);
                  }),
                  tap((updatedEquipment) => {
                    toastrService.success(
                      'equipment.ui.updatesuccess.notification',
                    );
                    patchState(store, {
                      equipment: updatedEquipment,
                      viewMode: 'detail',
                      shouldRefetch: true,
                      isLoading: false,
                    });
                  }),
                  handleGlobalError({
                    onError: () =>
                      patchState(store, {
                        isLoading: false,
                      }),
                  }),
                );
            }),
          ),
        ),
        updateEquipmentById: rxMethod<{
          endpointConfig: IWHEquipmentEndpointConfigModel;
          payload: Partial<IWHEquipmentDTO>;
        }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ endpointConfig, payload }) => {
              return equipmentDataService
                .updateEquipmentById(endpointConfig, payload)
                .pipe(
                  handleGlobalError({
                    onError: () =>
                      patchState(store, {
                        isLoading: false,
                      }),
                  }),
                  tap((updatedEquipment) => {
                    toastrService.success(
                      'equipment.ui.updatesuccess.notification',
                    );
                    patchState(store, {
                      equipment: updatedEquipment,
                      shouldRefetch: true,
                      isLoading: false,
                    });
                  }),
                );
            }),
          ),
        ),
        updateEquipmentByEquipmentDTO: rxMethod<{
          equipmentId: string;
          payload: IWHEquipmentDTO;
        }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ equipmentId, payload }) => {
              return equipmentService
                .updateEquipmentByEquipmentDTO(equipmentId, payload)
                .pipe(
                  handleGlobalError({
                    onError: () =>
                      patchState(store, {
                        isLoading: false,
                        assignmentInProcess: false,
                      }),
                  }),
                  tap((updatedEquipment) => {
                    toastrService.success(
                      'equipment.ui.updatesuccess.notification',
                    );
                    patchState(store, {
                      equipment: updatedEquipment,
                      isLoading: false,
                      assignmentInProcess: false,
                    });
                  }),
                );
            }),
          ),
        ),
        downloadQRCode: rxMethod<void>(
          pipe(
            switchMap(() => {
              return equipmentService
                .loadQRCode(
                  !store.isRootNode()
                    ? store.currentNode().qrCodeURL
                    : store.equipment()?.qrCodeURL,
                )
                .pipe(
                  tap((qrBlob) => {
                    equipmentDataService.saveQRCode(qrBlob, store.qrCodeName());
                  }),
                  handleGlobalError(),
                );
            }),
          ),
        ),
        createEquipment: rxMethod<{
          endpointConfig: IWHEquipmentEndpointConfigModel;
          payload: Partial<IWHEquipmentDTO>;
          imageFile?: { blob: Blob; file: File };
        }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ endpointConfig, payload, imageFile }) => {
              return equipmentDataService
                .createEquipment(endpointConfig, payload)
                .pipe(
                  handleGlobalError({
                    onError: () =>
                      patchState(store, {
                        isLoading: false,
                      }),
                  }),
                  switchMap((createdEquipment) => {
                    // Handle image upload for newly created equipment
                    if (imageFile && createdEquipment.rootBomNodeId) {
                      return uploadPictureToTargets(
                        {
                          bomNodeId: createdEquipment.rootBomNodeId,
                          equipmentId: createdEquipment.id,
                        },
                        imageFile,
                      ).pipe(
                        switchMap(() =>
                          equipmentService.getEquipmentDtoByEquimentId(
                            createdEquipment.id,
                          ),
                        ),
                        catchError((err) => {
                          // Still return the created equipment if image upload fails
                          return of(createdEquipment);
                        }),
                      );
                    }

                    return of(createdEquipment);
                  }),
                  switchMap((createdEquipment) => {
                    patchState(store, {
                      equipment: createdEquipment,
                    });

                    formReferenceService.unsavedChanges = false;

                    return bomNodeDataService.getBomNodeDTO(
                      {
                        apiUrl: environment.apiUrl + FLOW_GATEWAY,
                        bomNodeId: createdEquipment.rootBomNodeId,
                      },
                      skillsService.featureConfigMap$.value.get(
                        WHFeatureKeyENUM.SKILL_MANAGEMENT,
                      ),
                    );
                  }),
                  tap((bomNode) => {
                    patchState(store, {
                      currentNode: bomNode,
                      viewMode: 'detail',
                      isLoading: false,
                    });

                    toastrService.success(
                      'equipment.ui.createsuccess.notification',
                    );
                    router.navigate([
                      '/',
                      WHRouteZeroENUM.EQUIPMENTS,
                      bomNode.id,
                    ]);
                  }),
                );
            }),
          ),
        ),
        createBomNode: rxMethod<{
          endpointConfig: IWHBomNodeEndpointConfigModel;
          payload: Partial<IWHBomNodeDTO>;
          imageFile?: { blob: Blob; file: File };
        }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ endpointConfig, payload, imageFile }) => {
              return bomNodeDataService
                .createBomNode(endpointConfig, payload)
                .pipe(
                  handleGlobalError({
                    onError: () =>
                      patchState(store, {
                        isLoading: false,
                      }),
                  }),
                  switchMap((createdBomNode) => {
                    // Handle image upload for newly created bom node
                    if (imageFile && createdBomNode.id) {
                      return uploadPictureToTargets(
                        {
                          bomNodeId: createdBomNode.id,
                        },
                        imageFile,
                      ).pipe(
                        switchMap(() => of(createdBomNode)),
                        catchError((err) => {
                          // Still return the created node if image upload fails
                          return of(createdBomNode);
                        }),
                      );
                    }

                    return of(createdBomNode);
                  }),
                  tap((createdBomNode) => {
                    toastrService.success('node.ui.createsuccess.notification');
                    patchState(store, {
                      currentNode: createdBomNode,
                      shouldRefetch: true,
                      viewMode: 'detail',
                      createConfig: null,
                      nodeTree: null,
                      nodeTrees: [],
                      isLoading: false,
                    });

                    router.navigate([
                      '/',
                      WHRouteZeroENUM.EQUIPMENTS,
                      createdBomNode.id,
                    ]);
                  }),
                );
            }),
          ),
        ),
        updateBomNode: rxMethod<{
          endpointConfig: IWHBomNodeEndpointConfigModel;
          payload: Partial<IWHBomNodeDTO>;
          imageFile?: { blob: Blob; file: File };
          removeImage?: boolean;
        }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ endpointConfig, payload, imageFile, removeImage }) => {
              return bomNodeDataService
                .updateBomNode(endpointConfig, payload)
                .pipe(
                  handleGlobalError({
                    onError: () =>
                      patchState(store, {
                        isLoading: false,
                      }),
                  }),
                  switchMap((updatedBomNode) => {
                    // Handle image removal
                    if (removeImage && updatedBomNode.id) {
                      const bomNodeEndpointConfig: IWHBomNodeEndpointConfigModel =
                        {
                          apiUrl: environment.apiUrl + FLOW_GATEWAY,
                          bomNodeId: updatedBomNode.id,
                        };
                      return bomNodeDataService
                        .removeImageFile(bomNodeEndpointConfig)
                        .pipe(
                          tap(() => {
                            updatedBomNode.pictureURL = undefined;
                            updatedBomNode.thumbnailURL = undefined;
                            updatedBomNode.bomNodePicture = null;
                          }),
                          switchMap(() => of(updatedBomNode)),
                          catchError((err) => {
                            // Still return the updated node if image removal fails
                            return of(updatedBomNode);
                          }),
                        );
                    }

                    // Handle image upload
                    if (imageFile && updatedBomNode.id) {
                      return uploadPictureToTargets(
                        {
                          bomNodeId: updatedBomNode.id,
                        },
                        imageFile,
                      ).pipe(
                        switchMap(() => of(updatedBomNode)),
                        catchError((err) => {
                          // Still return the updated node if image upload fails
                          return of(updatedBomNode);
                        }),
                      );
                    }

                    return of(updatedBomNode);
                  }),
                  tap((updatedBomNode) => {
                    toastrService.success('node.ui.updatesuccess.notification');
                    patchState(store, {
                      currentNode: updatedBomNode,
                      shouldRefetch: true,
                      createConfig: null,
                      viewMode: 'detail',
                      nodeTree: null,
                      nodeTrees: [],
                      isLoading: false,
                    });
                  }),
                );
            }),
          ),
        ),
        deleteBomNode: rxMethod<{
          endpointConfig: IWHBomNodeEndpointConfigModel;
        }>(
          pipe(
            tap(() => patchState(store, { isLoading: true })),
            switchMap(({ endpointConfig }) => {
              return bomNodeDataService
                .deleteDecommissionBomNode(endpointConfig, false)
                .pipe(
                  handleGlobalError({
                    onError: () =>
                      patchState(store, {
                        isLoading: false,
                      }),
                  }),
                  tap(() => {
                    toastrService.success('ui.deletesuccess.notification', {
                      params: {
                        object: `${store.currentNode()?.extId} - ${store.currentNode()?.name}`,
                      },
                    });

                    if (store.isRootNode()) {
                      router.navigate(['/', WHRouteZeroENUM.EQUIPMENTS]);
                    } else {
                      router.navigate([
                        '/',
                        WHRouteZeroENUM.EQUIPMENTS,
                        store.currentNode().parentNodeId,
                        store.equipment().id,
                      ]);
                    }
                    patchState(store, {
                      isLoading: false,
                    });
                  }),
                );
            }),
          ),
        ),
      };
    },
  ),
);
